import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { ConfirmDialog, EmptyState, LoadingSpinner } from '../components/common';
import {
  useCreateProductStack,
  useDeleteProductStack,
  useProductStackOperations,
  useProductStacks,
} from '../hooks/useProductStacks';
import type {
  CreateProductStackDto,
  ProductStackDto,
  ProductStackLifecycleState,
  ProductStackOperationDto,
  ProductStackOperationStatus,
} from '../types/productStack';

const ADJECTIVES = [
  'amber',
  'brisk',
  'clear',
  'delta',
  'ember',
  'frost',
  'gold',
  'lunar',
  'mellow',
  'nova',
  'quiet',
  'swift',
];

const NOUNS = [
  'anchor',
  'beacon',
  'canyon',
  'forest',
  'harbor',
  'island',
  'lantern',
  'meadow',
  'orbit',
  'prairie',
  'river',
  'summit',
];

const IN_FLIGHT_STATES = new Set<ProductStackLifecycleState>([
  'Requested',
  'Validating',
  'ProvisioningInfra',
  'DeployingApps',
  'DeleteRequested',
  'Deleting',
]);

const DELETABLE_STATES = new Set<ProductStackLifecycleState>(['Running', 'Failed']);

interface ProductStackRowProps {
  stack: ProductStackDto;
  onDelete: (stack: ProductStackDto) => void;
  isDeletePending: boolean;
}

function ProductStackRow({ stack, onDelete, isDeletePending }: ProductStackRowProps) {
  const { data: operations = [] } = useProductStackOperations(stack.id);
  const latestOperation = operations[0];

  const deleteDisabled =
    isDeletePending ||
    !DELETABLE_STATES.has(stack.lifecycleState) ||
    latestOperation?.status === 'Pending' ||
    latestOperation?.status === 'Running';

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{stack.name}</td>
      <td className="px-4 py-3 text-sm">{renderLifecycleBadge(stack.lifecycleState)}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{stack.createdByDisplayName || 'Unknown'}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{new Date(stack.createdOn).toLocaleString()}</td>
      <td className="px-4 py-3 text-sm">{renderEndpointLink(stack.irisUrl)}</td>
      <td className="px-4 py-3 text-sm">{renderEndpointLink(stack.bifrostUrl)}</td>
      <td className="px-4 py-3 text-sm">{renderOperationState(latestOperation)}</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={() => onDelete(stack)}
          disabled={deleteDisabled}
          className="inline-flex items-center rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={deleteDisabled ? 'Delete unavailable while operation is in progress or state is incompatible' : 'Delete product stack'}
        >
          <TrashIcon className="mr-1 h-4 w-4" />
          Delete
        </button>
      </td>
    </tr>
  );
}

function buildRandomName(existing: Set<string>) {
  for (let i = 0; i < 60; i++) {
    const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
    const candidate = `${adjective}-${noun}`;
    if (!existing.has(candidate)) {
      return candidate;
    }
  }

  return `stack-${Date.now().toString().slice(-6)}`;
}

function renderLifecycleBadge(state: ProductStackLifecycleState) {
  const classes: Record<ProductStackLifecycleState, string> = {
    Requested: 'bg-slate-100 text-slate-700',
    Validating: 'bg-indigo-100 text-indigo-700',
    ProvisioningInfra: 'bg-cyan-100 text-cyan-700',
    DeployingApps: 'bg-blue-100 text-blue-700',
    Running: 'bg-green-100 text-green-700',
    DeleteRequested: 'bg-amber-100 text-amber-700',
    Deleting: 'bg-orange-100 text-orange-700',
    Deleted: 'bg-zinc-100 text-zinc-700',
    Failed: 'bg-red-100 text-red-700',
  };

  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${classes[state]}`}>{state}</span>;
}

function renderOperationBadge(status: ProductStackOperationStatus) {
  const classes: Record<ProductStackOperationStatus, string> = {
    Pending: 'bg-amber-100 text-amber-700',
    Running: 'bg-blue-100 text-blue-700',
    Succeeded: 'bg-green-100 text-green-700',
    Failed: 'bg-red-100 text-red-700',
  };

  return <span className={`rounded-full px-2 py-1 text-xs font-semibold ${classes[status]}`}>{status}</span>;
}

function renderOperationState(operation?: ProductStackOperationDto) {
  if (!operation) {
    return <span className="text-gray-500">No operations</span>;
  }

  return (
    <div>
      <div className="mb-1">{renderOperationBadge(operation.status)}</div>
      <div className="text-xs text-gray-600">
        {operation.operationType}: {operation.statusMessage || 'No details'}
      </div>
    </div>
  );
}

function renderEndpointLink(url?: string) {
  if (!url) {
    return <span className="text-gray-400">n/a</span>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900"
    >
      <LinkIcon className="h-4 w-4" />
      Open
    </a>
  );
}

export const ProductStacksPage: React.FC = () => {
  const [environmentName, setEnvironmentName] = useState('dev3');
  const [regionKey, setRegionKey] = useState('neu');
  const [name, setName] = useState('');
  const [enableCdm, setEnableCdm] = useState(true);
  const [deleteCandidate, setDeleteCandidate] = useState<ProductStackDto | null>(null);

  const { data: stacks = [], isLoading, error, refetch, isFetching } = useProductStacks();
  const createMutation = useCreateProductStack();
  const deleteMutation = useDeleteProductStack();

  const inFlightCount = useMemo(
    () => stacks.filter((stack) => IN_FLIGHT_STATES.has(stack.lifecycleState)).length,
    [stacks],
  );

  const existingActiveNames = useMemo(
    () => new Set(stacks.filter((stack) => stack.lifecycleState !== 'Deleted').map((stack) => stack.name.toLowerCase())),
    [stacks],
  );

  const generateName = () => {
    const suggested = buildRandomName(existingActiveNames);
    setName(suggested);
  };

  const createDisabled = createMutation.isPending || !name.trim();

  const handleCreate = async () => {
    const normalizedName = name.trim().toLowerCase();

    if (existingActiveNames.has(normalizedName)) {
      toast.error('A stack with this name already exists in active inventory.');
      return;
    }

    const payload: CreateProductStackDto = {
      name: normalizedName,
      environmentName: environmentName.trim(),
      regionKey: regionKey.trim(),
      enableCdm,
      createdByDisplayName: 'current-user',
    };

    try {
      await createMutation.mutateAsync(payload);
      toast.success('Product stack requested. Provisioning has started.');
      setName('');
    } catch (mutationError) {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to create stack';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(deleteCandidate.id);
      toast.success(`Teardown requested for ${deleteCandidate.name}.`);
      setDeleteCandidate(null);
    } catch (mutationError) {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to request teardown';
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="mb-3 flex items-center gap-2 text-red-700">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span className="font-semibold">Failed to load Product Stacks</span>
        </div>
        <p className="mb-4 text-sm text-red-700">{error instanceof Error ? error.message : 'Unknown error'}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
        >
          <ArrowPathIcon className="mr-2 h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Stacks</h1>
          <p className="text-sm text-gray-600">Create and manage isolated ACA stack instances for internal VPN environments.</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Create Product Stack</h2>
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Stack Name</label>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="cave-banana"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Environment</label>
            <input
              value={environmentName}
              onChange={(event) => setEnvironmentName(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Region</label>
            <input
              value={regionKey}
              onChange={(event) => setRegionKey(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={generateName}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <PlusIcon className="mr-1 h-4 w-4" />
              Suggest Name
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={createDisabled}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={enableCdm}
            onChange={(event) => setEnableCdm(event.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Enable CDM
        </label>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3 text-sm text-gray-600">
          {stacks.length} stack{stacks.length === 1 ? '' : 's'} total, {inFlightCount} in progress
        </div>

        {!stacks.length ? (
          <EmptyState
            title="No Product Stacks"
            description="Create your first stack to start provisioning internal environments."
            action={{
              label: 'Suggest Name',
              onClick: generateName,
            }}
            icon={<PlusIcon className="h-12 w-12" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Created On</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Iris URL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Bifrost URL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Operation</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {stacks.map((stack) => (
                  <ProductStackRow
                    key={stack.id}
                    stack={stack}
                    onDelete={setDeleteCandidate}
                    isDeletePending={deleteMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={!!deleteCandidate}
        onClose={() => setDeleteCandidate(null)}
        onConfirm={handleDelete}
        title="Delete Product Stack"
        message={`Request teardown for ${deleteCandidate?.name}? This operation is asynchronous and progress will continue in the operation state.`}
        confirmLabel={deleteMutation.isPending ? 'Deleting...' : 'Request Delete'}
        isDestructive
      />
    </div>
  );
};
